"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AllegroTransformComponentGenerator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformProject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformComponent_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponent");
const TransformComponentSymbol_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponentSymbol");
const cssParser_1 = require("../../../parsers/css/cssParser");
const allegroLayers_1 = require("../../../allegroLayers");
const AllegroResolved_1 = require("../AllegroResolved");
const { SchLayerType } = sigma_engine_shared_1.DesignModels;
const PLACEHOLDER_PIN_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.Pin].id;
const PIN_NAME_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.PinName].id;
const PIN_NUMBER_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.PinNumber].id;
const NAME_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.NameLabel].id;
const VALUE_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.ValueLabel].id;
const PLACEHOLDER_SYMBOL_BODY_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.Symbol].id;
const PLACEHOLDER_SYMBOL_HALF_WIDTH = 2.5;
const PLACEHOLDER_PIN_LENGTH = 1;
const PLACEHOLDER_PIN_SPACING = 2.5;
class AllegroTransformComponentGenerator {
    constructor() {
        this.runsFor = [TransformProject_1.TransformProject];
    }
    *generate(project, rootContext) {
        const bodyStroke = rootContext.resolved.symbolBodyStroke;
        const instPropTextStyle = rootContext.resolved.instPropTextStyle;
        const pinPropTextStyle = rootContext.resolved.pinPropTextStyle;
        for (const resolved of rootContext.resolved.components) {
            yield* this.emitComponent(project, rootContext, resolved, bodyStroke, instPropTextStyle, pinPropTextStyle);
        }
    }
    *emitComponent(project, rootContext, resolved, bodyStroke, instPropTextStyle, pinPropTextStyle) {
        const numPins = resolved.isVirtual ?
            resolved.views.reduce((sum, v) => sum + v.terms.length, 0)
            : (resolved.views[0]?.terms.length ?? 0);
        const component = new TransformComponent_1.TransformComponent(project, {
            baseComponent: {
                name: resolved.cellName,
                numPins,
                numPads: 0,
                footprints: [],
                symbols: [],
                signals: [],
                ...(resolved.isVirtual ? { isVirtual: true } : {}),
            },
        });
        yield component;
        if (!rootContext.componentsByCellName.has(resolved.cellName)) {
            rootContext.componentsByCellName.set(resolved.cellName, component);
        }
        const hasMultipleSections = new Set(resolved.views.map((v) => v.sectionNumber)).size > 1;
        for (const view of resolved.views) {
            const viewOpts = this.buildSymbolOptionsForView(resolved, view, hasMultipleSections);
            yield this.buildSymbol(component, resolved, view, viewOpts, bodyStroke, instPropTextStyle, pinPropTextStyle);
        }
    }
    buildSymbolOptionsForView(resolved, view, hasMultipleSections) {
        if (resolved.isVirtual) {
            return { pinNumberSuffix: `@${view.view}`, omitVisiblePinNumber: true };
        }
        if (hasMultipleSections) {
            return { signalIdSuffix: `@s${view.sectionNumber}` };
        }
        return {};
    }
    buildSymbol(parent, resolved, view, options, bodyStroke, instPropTextStyle, pinPropTextStyle) {
        const pinNumberFor = (logicalName) => {
            const fromView = view.pinNumberByLogical.get(logicalName);
            if (fromView !== undefined && fromView.length > 0) {
                return fromView;
            }
            return logicalName;
        };
        const pins = view.css !== null ?
            this.buildPinsFromCss(view, pinNumberFor, options, pinPropTextStyle)
            : view.terms.map((term, idx) => this.buildPlaceholderPin(term.name, pinNumberFor(term.name), idx, view.terms.length, options, pinPropTextStyle));
        const bodyDrawings = view.css !== null ? this.buildBodyDrawingsFromCss(view.css, bodyStroke) : [];
        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(parent, {
            ref: (0, AllegroResolved_1.allegroSymbolRefForCell)(resolved.cellName, view.view, view.sectionNumber),
            name: resolved.cellName,
            pins,
            drawings: [...bodyDrawings, ...this.buildLabelPlaceholders(instPropTextStyle)],
        });
        const sigSuffix = options.signalIdSuffix ?? '';
        for (const pin of pins) {
            const id = sigSuffix.length > 0 ? `${pin.name}${sigSuffix}` : pin.name;
            symbol.setPinSignal(pin, { id, name: pin.name });
        }
        return symbol;
    }
    buildLabelPlaceholders(textStyle) {
        const labelOffset = PLACEHOLDER_SYMBOL_HALF_WIDTH + textStyle.size * 2;
        const colorOverride = textStyle.color !== null ? { colorOverride: textStyle.color } : {};
        const attr = [];
        if (textStyle.bold) {
            attr.push('bold');
        }
        if (textStyle.italic) {
            attr.push('italic');
        }
        const attrField = attr.length > 0 ? { attr } : {};
        return [
            {
                layers: [NAME_LABEL_LAYER],
                drawing: {
                    type: 'text',
                    ref: 'Part Reference',
                    ref_designator: true,
                    value: '?',
                    pos: [0, -labelOffset],
                    size: textStyle.size,
                    justify: [-1, -1],
                    thickness: 1,
                    ...colorOverride,
                    ...attrField,
                },
            },
            {
                layers: [VALUE_LABEL_LAYER],
                drawing: {
                    type: 'text',
                    ref: 'Value',
                    ref_designator: true,
                    value: '?',
                    pos: [0, labelOffset],
                    size: textStyle.size,
                    justify: [-1, -1],
                    thickness: 1,
                    ...colorOverride,
                    ...attrField,
                },
            },
        ];
    }
    buildPinsFromCss(view, pinNumberFor, options, pinTextStyle) {
        const suffix = options.pinNumberSuffix ?? '';
        const omitNumberDrawing = options.omitVisiblePinNumber ?? false;
        return view.terms.map((term, idx) => {
            const cssPin = view.pinByLower.get(term.name.toLowerCase());
            const physicalNumber = pinNumberFor(term.name);
            if (cssPin === undefined) {
                return this.buildPlaceholderPin(term.name, physicalNumber, idx, view.terms.length, options, pinTextStyle);
            }
            const bodyEnd = this.cssToMm(cssPin.bodyX, cssPin.bodyY);
            const hotEnd = this.cssToMm(cssPin.hotX, cssPin.hotY);
            const refWithSuffix = suffix.length > 0 ? `${physicalNumber}${suffix}` : physicalNumber;
            return {
                type: 'pin',
                ref: refWithSuffix,
                name: term.name,
                number: refWithSuffix,
                start: bodyEnd,
                end: hotEnd,
                layer: PLACEHOLDER_PIN_LAYER,
                drawings: this.buildPinTextDrawings(term.name, physicalNumber, bodyEnd, hotEnd, omitNumberDrawing, pinTextStyle),
            };
        });
    }
    buildBodyDrawingsFromCss(css, bodyStroke) {
        const colorOverride = bodyStroke.color !== null ? { colorOverride: bodyStroke.color } : {};
        const segments = css.lines.map((line) => ({
            layers: [PLACEHOLDER_SYMBOL_BODY_LAYER],
            drawing: {
                type: 'segment',
                start: this.cssToMm(line.x1, line.y1),
                end: this.cssToMm(line.x2, line.y2),
                width: bodyStroke.width,
                ...colorOverride,
            },
        }));
        const arcs = css.arcs.map((arc) => {
            const center = this.cssToMm(arc.cx, arc.cy);
            const radius = arc.radius * cssParser_1.CSS_UNIT_TO_MM;
            if (Math.abs(arc.endDeg - arc.startDeg) >= 360 - 1e-3) {
                return {
                    layers: [PLACEHOLDER_SYMBOL_BODY_LAYER],
                    drawing: { type: 'circle', center, radius, filled: false, width: bodyStroke.width, ...colorOverride },
                };
            }
            return {
                layers: [PLACEHOLDER_SYMBOL_BODY_LAYER],
                drawing: {
                    type: 'arc',
                    center,
                    radius,
                    startAngle: -arc.endDeg,
                    endAngle: -arc.startDeg,
                    lineWidth: bodyStroke.width,
                    ...colorOverride,
                },
            };
        });
        return [...segments, ...arcs];
    }
    cssToMm(x, y) {
        return [x * cssParser_1.CSS_UNIT_TO_MM, -y * cssParser_1.CSS_UNIT_TO_MM];
    }
    buildPlaceholderPin(termName, physicalPinNumber, pinIndex, totalPins, options, pinTextStyle) {
        const suffix = options.pinNumberSuffix ?? '';
        const omitNumberDrawing = options.omitVisiblePinNumber ?? false;
        const sideCount = Math.ceil(totalPins / 2);
        const onLeftSide = pinIndex % 2 === 0;
        const sideIndex = Math.floor(pinIndex / 2);
        const verticalSpan = (sideCount - 1) * PLACEHOLDER_PIN_SPACING;
        const y = -verticalSpan / 2 + sideIndex * PLACEHOLDER_PIN_SPACING;
        const bodyEdgeX = onLeftSide ? -PLACEHOLDER_SYMBOL_HALF_WIDTH : PLACEHOLDER_SYMBOL_HALF_WIDTH;
        const outsideX = onLeftSide ?
            -(PLACEHOLDER_SYMBOL_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH)
            : PLACEHOLDER_SYMBOL_HALF_WIDTH + PLACEHOLDER_PIN_LENGTH;
        const bodyEnd = [bodyEdgeX, y];
        const hotEnd = [outsideX, y];
        const refWithSuffix = suffix.length > 0 ? `${physicalPinNumber}${suffix}` : physicalPinNumber;
        return {
            type: 'pin',
            ref: refWithSuffix,
            name: termName,
            number: refWithSuffix,
            start: bodyEnd,
            end: hotEnd,
            layer: PLACEHOLDER_PIN_LAYER,
            drawings: this.buildPinTextDrawings(termName, physicalPinNumber, bodyEnd, hotEnd, omitNumberDrawing, pinTextStyle),
        };
    }
    buildPinTextDrawings(pinName, pinNumber, bodyEnd, hotEnd, omitVisiblePinNumber, textStyle) {
        const [bodyX, bodyY] = bodyEnd;
        const [hotX, hotY] = hotEnd;
        let angle;
        let nameJustify;
        const dx = hotX - bodyX;
        const dy = hotY - bodyY;
        if (Math.abs(dx) >= Math.abs(dy)) {
            angle = 0;
            nameJustify = dx > 0 ? [1, 0] : [-1, 0];
        }
        else {
            angle = 90;
            nameJustify = dy > 0 ? [-1, 0] : [1, 0];
        }
        const size = textStyle.size;
        const estHalfWidth = (pinName.length * size * 0.6) / 2;
        const perpOffset = size * 1.2;
        const nameOffset = angle === 0 ? [estHalfWidth * -nameJustify[0], 0] : [0, estHalfWidth * nameJustify[0]];
        const numberOffset = angle === 0 ? [0, -perpOffset] : [-perpOffset, 0];
        const midX = (bodyX + hotX) / 2;
        const midY = (bodyY + hotY) / 2;
        const colorOverride = textStyle.color !== null ? { colorOverride: textStyle.color } : {};
        const attr = [];
        if (textStyle.bold) {
            attr.push('bold');
        }
        if (textStyle.italic) {
            attr.push('italic');
        }
        const attrField = attr.length > 0 ? { attr } : {};
        const drawings = [
            {
                layers: [PIN_NAME_LAYER],
                drawing: {
                    type: 'text',
                    value: pinName,
                    pos: [bodyX + nameOffset[0], bodyY + nameOffset[1]],
                    angle,
                    justify: [0, 0],
                    size,
                    thickness: 1,
                    rejustifyOnRotate: true,
                    ...colorOverride,
                    ...attrField,
                },
            },
        ];
        if (!omitVisiblePinNumber) {
            drawings.push({
                layers: [PIN_NUMBER_LAYER],
                drawing: {
                    type: 'text',
                    value: pinNumber,
                    pos: [midX + numberOffset[0], midY + numberOffset[1]],
                    angle,
                    justify: [0, 0],
                    size,
                    thickness: 1,
                    rejustifyOnRotate: true,
                    ...colorOverride,
                    ...attrField,
                },
            });
        }
        return drawings;
    }
}
exports.AllegroTransformComponentGenerator = AllegroTransformComponentGenerator;
//# sourceMappingURL=AllegroTransformComponentGenerator.js.map